import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { buildBreadcrumbs } from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import StructuredData, { getWebPageSchema } from '@/components/feature/StructuredData';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const [activeTab, setActiveTab] = useState<string>(slug || '');

  const articles = useMemo(() => {
    if (!slug) return [];
    return getArticlesByCategory(slug);
  }, [slug]);

  const breadcrumbs = useMemo(
    () => buildBreadcrumbs(`/category/${slug}`, slug),
    [slug],
  );

  useEffect(() => {
    if (category) {
      document.title = `${category.name} - 打屁股视频大全 | 精选视频合集`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc)
        metaDesc.setAttribute('content', category.description);
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical)
        canonical.setAttribute('href', `https://dapiguvideo.com/category/${slug}`);
    }
  }, [category, slug]);

  useEffect(() => {
    setActiveTab(slug || '');
  }, [slug]);

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">分类未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您访问的分类不存在。</p>
            <Link
              to="/"
              className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <StructuredData
        data={getWebPageSchema(
          `${category.name} - 打屁股视频大全`,
          category.description,
          `https://dapiguvideo.com/category/${slug}`,
        )}
      />

      <Navbar />

      <main className="pt-16">
        {/* Category Header */}
        <section className="relative w-full h-[240px] md:h-[320px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20abstract%20geometric%20pattern%20with%20soft%20flowing%20curves%2C%20amber%20cream%20and%20beige%20color%20palette%2C%20elegant%20minimal%20design%2C%20smooth%20gradient%20background%2C%20modern%20aesthetic%2C%20subtle%20texture&width=1600&height=700&seq=cat-header-2026&orientation=landscape"
            alt={category.name}
            title={category.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/40"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-50/20 backdrop-blur-sm mb-4">
              <i className={`${category.icon} text-2xl text-background-50`}></i>
            </span>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-3">
              {category.name}
            </h1>
            <p className="text-sm md:text-base text-background-50/80 max-w-xl">
              {category.description}
            </p>
            <p className="text-xs text-background-50/60 mt-2">
              共 {category.articleCount} 篇内容
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="bg-background-50 border-b border-background-200/70 sticky top-16 z-40">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-3 overflow-x-auto">
            <div className="flex items-center gap-2 flex-nowrap min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbs} />
          </div>

          {articles.length === 0 ? (
            <div className="text-center py-20">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </span>
              <p className="text-foreground-500">该分类暂无内容</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}